import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Switch, FormControlLabel, TextField, Button,
  Grid, Chip, Autocomplete, Alert, CircularProgress, InputAdornment, Divider
} from '@mui/material';
import axios from 'axios';
import { useNotification } from '../context/NotificationContext';

const API = process.env.REACT_APP_API_URL;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

/**
 * On-Time Arrival Incentive Program settings (owners only).
 * Global toggle + flat dollar bonuses per arrival tier + per-duty ETA
 * eligibility caps + per-SP exclusions. When enabled, new jobs get the
 * program notice prepended to their billing notes and completed SP jobs
 * earn the bonus automatically on their invoice.
 */
function IncentiveProgram() {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [notePreview, setNotePreview] = useState('');
  const [spOptions, setSpOptions] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/incentive-config`, { headers: getAuthHeader() });
      setConfig(data.config);
      setNotePreview(data.notePreview);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to load incentive settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  // SP list for the exclusion picker (vendorNumber + company name)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/users?primaryRole=SP&limit=500`, { headers: getAuthHeader() });
        const users = Array.isArray(data) ? data : (data.users || data.data || []);
        const options = users
          .filter((u) => u.vendorNumber || u.vendorId)
          .map((u) => ({
            vendorNumber: u.vendorNumber || u.vendorId,
            label: `${u.companyName || `${u.firstName || ''} ${u.lastName || ''}`.trim()} (${u.vendorNumber || u.vendorId})`
          }));
        // De-dupe by vendorNumber
        const seen = new Set();
        setSpOptions(options.filter((o) => !seen.has(o.vendorNumber) && seen.add(o.vendorNumber)));
      } catch {
        // Exclusion picker degrades to free-text entry
      }
    })();
  }, []);

  const setField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));
  const setCap = (duty, value) => setConfig((prev) => ({
    ...prev, etaCaps: { ...prev.etaCaps, [duty]: value }
  }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/api/incentive-config`, {
        enabled: config.enabled,
        earlyBonus: Number(config.earlyBonus),
        onTimeBonus: Number(config.onTimeBonus),
        graceBonus: Number(config.graceBonus),
        etaCaps: {
          light: Number(config.etaCaps?.light),
          medium: Number(config.etaCaps?.medium),
          heavy: Number(config.etaCaps?.heavy)
        },
        excludedVendorNumbers: config.excludedVendorNumbers || []
      }, { headers: getAuthHeader() });
      setConfig(data.config);
      setNotePreview(data.notePreview);
      showNotification('Incentive program settings saved', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to save incentive settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const excludedValues = (config.excludedVendorNumbers || []).map((vn) =>
    spOptions.find((o) => o.vendorNumber === vn) || { vendorNumber: vn, label: vn }
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>On-Time Arrival Incentive Program</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rewards service providers for arriving early, on time, or close to on time,
        measured when the driver marks On Site against the accepted ETA. Bonuses are
        added automatically to the invoice of completed jobs and included in payouts.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!config.enabled}
              onChange={(e) => setField('enabled', e.target.checked)}
              color="success"
            />
          }
          label={
            <Box>
              <Typography variant="subtitle1">
                Program {config.enabled ? 'ON' : 'OFF'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                When on, new jobs are flagged in their billing notes and completed SP jobs earn bonuses.
              </Typography>
            </Box>
          }
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>Bonus amounts (flat, per job)</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label={`Early (${config.earlyThresholdMinutes}+ min before ETA)`}
              type="number" value={config.earlyBonus}
              onChange={(e) => setField('earlyBonus', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="On-Time (by the ETA)"
              type="number" value={config.onTimeBonus}
              onChange={(e) => setField('onTimeBonus', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label={`Grace (up to ${config.graceThresholdMinutes} min late)`}
              type="number" value={config.graceBonus}
              onChange={(e) => setField('graceBonus', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" gutterBottom>
          ETA eligibility caps (anti-gaming)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          A job only qualifies when the accepted ETA was at or under the cap for its duty
          class — padding the quote to arrive "early" earns nothing.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {['light', 'medium', 'heavy'].map((duty) => (
            <Grid item xs={12} sm={4} key={duty}>
              <TextField
                fullWidth
                label={`${duty.charAt(0).toUpperCase() + duty.slice(1)} duty cap`}
                type="number" value={config.etaCaps?.[duty] ?? ''}
                onChange={(e) => setCap(duty, e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                inputProps={{ min: 15, max: 240 }}
              />
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle1" gutterBottom>Excluded service providers</Typography>
        <Autocomplete
          multiple
          options={spOptions}
          value={excludedValues}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.vendorNumber === v.vendorNumber}
          onChange={(e, values) => setField('excludedVendorNumbers', values.map((v) => v.vendorNumber))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.label} {...getTagProps({ index })} key={option.vendorNumber} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Select SPs excluded from the program" />
          )}
          sx={{ mb: 2 }}
        />

        <Alert severity="info" icon={false} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          <Typography variant="caption" color="text.secondary">
            Billing-note preview (prepended to every new job, visible to admins/dispatchers only):
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{notePreview}</Typography>
        </Alert>

        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Paper>
    </Box>
  );
}

export default IncentiveProgram;
